import { useRef } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "src/design";
import {
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  radiusCard,
} from "src/design/tokens";

interface MenuQrCodeModalProps {
  open: boolean;
  onClose: () => void;
  url: string;
  restaurantName: string;
}

const QR_SIZE = 220;

const slugifyFileName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "menu";

export const MenuQrCodeModal = ({
  open,
  onClose,
  url,
  restaurantName,
}: MenuQrCodeModalProps) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `menu-qr-${slugifyFileName(restaurantName)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const printWindow = window.open("", "_blank", "width=480,height=640");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>${restaurantName}</title>
          <style>
            body {
              margin: 0;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              font-family: Arial, Helvetica, sans-serif;
              color: ${colorTextPrimary};
            }
            h1 { font-size: 20px; margin: 0 0 4px; }
            p { font-size: 13px; color: ${colorTextMuted}; margin: 0 0 24px; }
            img { width: 260px; height: 260px; }
            .url { font-size: 12px; color: ${colorTextMuted}; margin-top: 16px; word-break: break-all; }
          </style>
        </head>
        <body>
          <h1>${restaurantName}</h1>
          <p>${t("menu.qr.scanHint")}</p>
          <img src="${dataUrl}" alt="QR" />
          <div class="url">${url}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    // Give the embedded image a tick to decode before invoking print.
    printWindow.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
              {t("menu.qr.title")}
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: colorTextMuted, mt: 0.25 }}>
              {t("menu.qr.subtitle")}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: colorSurface,
              border: `1px solid ${colorBorder}`,
              borderRadius: radiusCard,
              lineHeight: 0,
            }}
          >
            <QRCodeCanvas
              ref={canvasRef}
              value={url}
              size={QR_SIZE}
              level="M"
              marginSize={2}
            />
          </Box>

          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: colorTextPrimary }}>
            {restaurantName}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: colorTextMuted }}>
            {t("menu.qr.scanHint")}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: colorTextMuted,
              wordBreak: "break-all",
              maxWidth: 320,
            }}
          >
            {url}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <CustomButton
          color="inverted"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          {t("menu.qr.download")}
        </CustomButton>
        <CustomButton
          color="black"
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          {t("menu.qr.print")}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
